/**
 * Names the upcoming Visits the reschedule dialog offers to join: date, time and crew in the Visit's zone, whether the work joins or lands beside it, and why a target
 * is refused. Pure, so the wording is tested without mounting the dialog.
 */
import { DateTime } from 'luxon';
import { VisitMoveSeparateReason, VisitMoveTarget, VisitMoveTargetOutcome } from '../visit.movement.types';

/** One row of the target picker; a refused target is listed but cannot be chosen. */
export interface VisitMoveTargetOption {
  value: string;
  /** "Tue 4 Nov · 08:00–12:00". */
  label: string;
  /** Crew, then "Joins this visit" / "Separate visit (different time)", or the refusal. */
  description: string;
  disabled: boolean;
}

/** Shared with the preview's separate-visit wording, so both say the same thing. */
export const VISIT_SEPARATE_REASON_LABELS: Record<VisitMoveSeparateReason, string> = {
  [VisitMoveSeparateReason.Time]: 'different time',
  [VisitMoveSeparateReason.Crew]: 'different crew',
  [VisitMoveSeparateReason.Contractor]: 'different contractor',
  [VisitMoveSeparateReason.JobDefaults]: 'different Job settings',
  [VisitMoveSeparateReason.Authority]: 'different approval',
};

/** The picker rows, in the server's appointment order. */
export function visitMoveTargetOptions(targets: readonly VisitMoveTarget[], timezone: string | undefined): VisitMoveTargetOption[] {

  return targets.map(target => ({
    value: target._id,
    label: visitMoveTargetLabel(target, timezone),
    description: targetDescription(target),
    disabled: target.refusals.length > 0,
  }));
}

/** "Tue 4 Nov · 08:00–12:00" or "Tue 4 Nov · All day", in the Visit's zone. */
export function visitMoveTargetLabel(target: VisitMoveTarget, timezone: string | undefined): string {

  const start = DateTime.fromJSDate(new Date(target.startDate), { zone: timezone });
  const end = DateTime.fromJSDate(new Date(target.endDate), { zone: timezone });
  const day = start.toFormat('ccc d LLL');
  if (target.allDay) return `${day} · All day`;

  return `${day} · ${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')}`;
}

/** The targets the work could join as it is, named for the extra-trip warning. */
export function joinableVisitLabels(targets: readonly VisitMoveTarget[], timezone: string | undefined): string[] {

  return targets
    .filter(target => target.outcome === VisitMoveTargetOutcome.Joins && !target.refusals.length)
    .map(target => visitMoveTargetLabel(target, timezone));
}

function targetDescription(target: VisitMoveTarget): string {

  if (target.refusals.length) return target.refusals.map(refusal => refusal.message).join(' ');
  const crew = target.resources.map(resource => resource.name).filter(Boolean).join(', ') || target.contractor?.name || 'No crew';
  const outcome = target.outcome === VisitMoveTargetOutcome.Joins
    ? 'Joins this visit'
    : `Separate visit (${(target.separateReasons ?? []).map(reason => VISIT_SEPARATE_REASON_LABELS[reason]).join(', ') || 'cannot share a visit'})`;

  return `${crew} · ${outcome}`;
}
